import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";

// Status color mapping
const getStatusColor = (status) => {
  const colors = {
    'Completed': '#10B981',
    'InProgress': '#3B82F6',
    'Pending': '#FBBF24',
    'Delayed': '#EF4444',
    'Unallocated': '#E5E7EB'
  };
  return colors[status] || '#9CA3AF';
};

// Exhibitor lists run to 100+ rows; show a workable slice inside the popup.
const EXHIBITOR_PREVIEW = 12;

const STATUS_LEGEND = [
  { status: 'Completed', color: '#10B981' },
  { status: 'InProgress', color: '#3B82F6' },
  { status: 'Pending', color: '#FBBF24' },
  { status: 'Delayed', color: '#EF4444' },
  { status: 'Unallocated', color: '#E5E7EB' },
];

// A hall can carry more than one row for the same service (one per vendor).
// Roll them up worst-first so a hall reads as done only when nothing is
// outstanding — the same way a person would read the grid.
const STATUS_SEVERITY = ['Delayed', 'Pending', 'InProgress', 'Completed'];
const worstStatus = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const ia = STATUS_SEVERITY.indexOf(a);
  const ib = STATUS_SEVERITY.indexOf(b);
  if (ia === -1) return b;
  if (ib === -1) return a;
  return ia <= ib ? a : b;
};

const hallKeyOf = (name) => String(name || "").trim().toLowerCase();

// Booths in a hall the service filter excluded stay visible but recede, so the
// drawing keeps its shape instead of losing landmarks the user navigates by.
const DIMMED_DOT_COLOR = '#D1D5DB';
const DIMMED_DOT_OPACITY = '0.25';

const paintBoothDot = (circle, status, dimmed) => {
  circle.setAttribute('fill', dimmed ? DIMMED_DOT_COLOR : status ? getStatusColor(status) : '#4f46e5');
  circle.setAttribute('opacity', dimmed ? DIMMED_DOT_OPACITY : '1');
};

// ─── Memoized SVG Component ─────────────────────────────────────────
// This prevents React from re-parsing the massive HTML string when SvgCanvas re-renders
const MemoizedSvg = React.memo(React.forwardRef(({ svgContent }, ref) => (
  <div
    ref={ref}
    style={{
      transformOrigin: "0 0",
      width: "100%",
      height: "100%",
    }}
    dangerouslySetInnerHTML={{ __html: svgContent }}
  />
)), (prev, next) => prev.svgContent === next.svgContent);

const normalizeId = (id) => String(id ?? "").trim().toLowerCase();

// sp_get_vendorauditTracker selects two columns with the same name (the
// exhibitor's and the vendor's), and the driver collapses each pair into an
// array: CompanyName: ["Acme Looms", "Grade Electricals"], MobileNo likewise.
// Index 0 is the exhibitor, index 1 the vendor. Scalars come back from the
// first recordset, where only the vendor column is selected.
const firstValue = (v) => (Array.isArray(v) ? v.find((x) => x != null && x !== "") : v);
const lastValue = (v) => {
  if (!Array.isArray(v)) return v;
  for (let i = v.length - 1; i >= 0; i--) {
    if (v[i] != null && v[i] !== "") return v[i];
  }
  return undefined;
};

// "2026-07-22 17:00" -> "22 Jul, 17:00"
const shortDateTime = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2})/);
  if (!m) return String(value);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]}, ${m[4]}`;
};

const taskState = (row) => {
  if (firstValue(row.IsCompleted)) return { label: "Completed", color: getStatusColor("Completed") };
  if (row.Overdue) return { label: "Overdue", color: getStatusColor("Delayed") };
  return { label: "Pending", color: getStatusColor("Pending") };
};

// A real datetime column, so it arrives tagged as UTC and must not be shifted a
// second time - the same reading the task screen's stamps go through
const verifiedOn = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2})/);
  if (!m) return String(value);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]}, ${m[4]}`;
};

// Join key for booth/stall codes across the drawing and the database. The
// drawing writes "H1-A1"; the database writes "H1A1". Punctuation is the only
// difference, so strip everything that isn't alphanumeric.
const normalizeBoothKey = (name) =>
  String(name ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const buildSvgIndex = (svgElement) => {
  const elementsByClip = new Map();
  const clipPathDefsById = new Map();
  const elementsByBoothName = new Map();
  const textElementsByContent = new Map();

  try {
    svgElement.querySelectorAll("clipPath[id]").forEach((cp) => {
      const id = normalizeId(cp.getAttribute("id"));
      if (id) clipPathDefsById.set(id, cp);
    });
  } catch (e) { }

  try {
    svgElement.querySelectorAll("[data-booth-name]").forEach((el) => {
      const name = normalizeId(el.getAttribute("data-booth-name"));
      if (!name) return;
      if (!elementsByBoothName.has(name)) elementsByBoothName.set(name, []);
      elementsByBoothName.get(name).push(el);
    });
  } catch (e) { }

  try {
    svgElement.querySelectorAll("text, tspan").forEach((el) => {
      const txt = normalizeId(el.textContent);
      if (!txt) return;
      if (!textElementsByContent.has(txt)) textElementsByContent.set(txt, []);
      textElementsByContent.get(txt).push(el);
    });
  } catch (e) { }

  const refRegex = /url\(\s*["']?#([^"')]+)["']?\s*\)/i;
  try {
    const candidates = svgElement.querySelectorAll('[clip-path], [style*="clip-path"], [style*="clip-Path"]');
    candidates.forEach((el) => {
      try {
        let raw = el.getAttribute("clip-path");
        if (!raw) {
          const styleAttr = el.getAttribute("style") || "";
          const m = styleAttr.match(/clip-path\s*:\s*([^;]+)/i);
          raw = m ? m[1] : null;
        }
        if (!raw) return;
        const match = raw.match(refRegex);
        if (!match) return;
        const id = normalizeId(match[1]);
        if (!id) return;
        if (!elementsByClip.has(id)) elementsByClip.set(id, []);
        elementsByClip.get(id).push(el);
      } catch (e) { }
    });
  } catch (e) { }

  return { elementsByClip, clipPathDefsById, elementsByBoothName, textElementsByContent };
};

// ─── Layout extraction from the drawing itself ──────────────────────────
// The layout is a PDF export: booth codes live in <text> nodes that carry
// fill-opacity="0" and sit invisibly on top of the vector glyphs. They are the
// only trustworthy booth anchors in the file - the API's ClipId values are
// sequential placeholders that mostly do not exist in the SVG, and the handful
// that do are full-canvas layer clips.
const BOOTH_LABEL_RE = /^H(\d+)\s*-\s*([A-Za-z0-9]+)$/i;
const HALL_TITLE_RE = /^HALLS?\s+(\d+)$/i;
// Groups we inject ourselves - never re-read them as layout content.
const OVERLAY_IDS = "#booth-focus, #booth-nodes, #booth-hits, #route-path";

const cleanText = (el) => (el.textContent || "").replace(/\s+/g, " ").trim();

// "H1-A10" must sort after "H1-A9", not between "H1-A1" and "H1-A2".
const naturalCompare = (a, b) => {
  const ax = String(a).match(/(\d+|\D+)/g) || [];
  const bx = String(b).match(/(\d+|\D+)/g) || [];
  for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
    const av = ax[i];
    const bv = bx[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    if (/^\d/.test(av) && /^\d/.test(bv)) {
      const diff = Number(av) - Number(bv);
      if (diff !== 0) return diff;
    } else if (av !== bv) {
      return av < bv ? -1 : 1;
    }
  }
  return 0;
};

const isOverlayNode = (el) => {
  try {
    return !!(el.closest && el.closest(OVERLAY_IDS));
  } catch (e) {
    return false;
  }
};

const extractSvgLayoutData = (svgElement) => {
  const elementsByBooth = new Map();
  const hallTitles = new Map();
  const boothsByName = new Map();

  let textNodes = [];
  try {
    textNodes = Array.from(svgElement.querySelectorAll("text"));
  } catch (e) {
    return { halls: [], booths: [], elementsByBooth };
  }

  textNodes.forEach((el) => {
    if (isOverlayNode(el)) return;
    const m = cleanText(el).match(HALL_TITLE_RE);
    if (m) hallTitles.set(m[1], `Hall ${m[1]}`);
  });

  textNodes.forEach((el) => {
    if (isOverlayNode(el)) return;
    const raw = cleanText(el);
    const m = raw.match(BOOTH_LABEL_RE);
    if (!m) return;

    const hallNo = m[1];
    const name = raw.toUpperCase().replace(/\s*-\s*/, "-");
    const hallId = `H${hallNo}`;

    if (!elementsByBooth.has(name)) elementsByBooth.set(name, []);
    elementsByBooth.get(name).push(el);

    if (!boothsByName.has(name)) {
      boothsByName.set(name, {
        BoothID: name,
        BoothName: name,
        HallID: hallId,
        HallName: hallTitles.get(hallNo) || `Hall ${hallNo}`,
        Block: (m[2].match(/^[A-Za-z]+/) || [""])[0].toUpperCase(),
      });
    }

    // Let the existing hover/click delegation find these. The nodes are
    // unpainted, so they also need pointer-events turned on (done in CSS).
    el.setAttribute("data-booth-name", name);
    el.setAttribute("data-hall-id", hallId);
  });

  const booths = Array.from(boothsByName.values()).sort((a, b) =>
    naturalCompare(a.BoothName, b.BoothName)
  );

  const hallMap = new Map();
  booths.forEach((b) => {
    if (!hallMap.has(b.HallID)) {
      hallMap.set(b.HallID, { HallID: b.HallID, HallName: b.HallName, BoothCount: 0 });
    }
    hallMap.get(b.HallID).BoothCount++;
  });
  const halls = Array.from(hallMap.values()).sort((a, b) =>
    naturalCompare(a.HallID, b.HallID)
  );

  return { halls, booths, elementsByBooth };
};

// Matrix from an element's own user space into the <svg> root's USER space,
// i.e. viewBox units - the space our overlays and the nav grid are expressed in.
//
// getCTM() is deliberately not used: it maps to the nearest *viewport*, which
// includes the viewBox->viewport transform. That is identity only while the svg
// renders at its intrinsic size; once it scales to its container the results
// come back in CSS pixels and every overlay lands in the wrong place. Composing
// root.getScreenCTM()^-1 with el.getScreenCTM() cancels the viewport transform
// (and any ancestor CSS transform) and leaves pure user units.
const matrixToSvgUserSpace = (el) => {
  try {
    const root = el.ownerSVGElement;
    if (root && typeof root.getScreenCTM === "function" && typeof el.getScreenCTM === "function") {
      const rootCTM = root.getScreenCTM();
      const elCTM = el.getScreenCTM();
      if (rootCTM && elCTM) return rootCTM.inverse().multiply(elCTM);
    }
  } catch (e) { }
  try {
    return typeof el.getCTM === "function" ? el.getCTM() : null;
  } catch (e) {
    return null;
  }
};

// getBBox() reports the element's own user space; map it into root user space.
const elementBoxInSvgSpace = (el) => {
  try {
    const bbox = el.getBBox();
    if (!(bbox.width > 0 || bbox.height > 0)) return null;
    const ctm = matrixToSvgUserSpace(el);
    if (!ctm) {
      return { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
    }
    const pts = [
      [bbox.x, bbox.y],
      [bbox.x + bbox.width, bbox.y],
      [bbox.x, bbox.y + bbox.height],
      [bbox.x + bbox.width, bbox.y + bbox.height],
    ].map(([x, y]) => ({
      x: ctm.a * x + ctm.c * y + ctm.e,
      y: ctm.b * x + ctm.d * y + ctm.f,
    }));
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
  } catch (e) {
    return null;
  }
};

const unionBoxes = (boxes) => {
  const valid = boxes.filter(Boolean);
  if (valid.length === 0) return null;
  const minX = Math.min(...valid.map((b) => b.x));
  const minY = Math.min(...valid.map((b) => b.y));
  const maxX = Math.max(...valid.map((b) => b.x + b.width));
  const maxY = Math.max(...valid.map((b) => b.y + b.height));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

// The drawing has no addressable booth outline, only the label. Grow the label
// box into a booth-cell-sized target so the focus ring reads as the stall
// rather than as the text.
const padBox = (box, padX = 7, padY = 7, minW = 24, minH = 15) => {
  if (!box) return null;
  let { x, y, width, height } = box;
  x -= padX; y -= padY; width += padX * 2; height += padY * 2;
  if (width < minW) { x -= (minW - width) / 2; width = minW; }
  if (height < minH) { y -= (minH - height) / 2; height = minH; }
  return { x, y, width, height };
};

// ─── Walkable navigation grid ───────────────────────────────────────────
// Routing cannot use a booth-to-booth graph: every edge in one is a straight
// line, so the shortest path cuts diagonally through the stalls. Nor can it use
// booth label boxes as obstacles - a label is a ~10x4 unit sliver sitting inside
// a stall that may be 30x25, leaving most of the stall walkable.
//
// Instead the drawing's own line work is rasterised as walls. Every stall in
// this layout is drawn as a closed (if dashed) outline, so once the ink is
// blocked each stall becomes a sealed pocket and the largest remaining free
// region *is* the aisle network. Routing is confined to that region, which
// makes passing through a stall structurally impossible rather than merely
// expensive. Measured on the ELECRAMA layout: 150/150 stalls sealed, 10/10
// sample routes entirely within the aisles.

const NAV_CELL = 2;        // SVG user units per cell
const MIN_INK_SEG = 1.5;   // shorter strokes are glyph outlines, not structure
const TURN_COST = 2.5;     // without this the optimal path is a staircase
const HUG_COST = 1.5;      // keeps the line off the walls, down the aisle centre

// Layers holding text rendered as outlines. Their glyph strokes would pepper
// the aisles with phantom obstacles.
const TEXT_LAYERS = new Set(["Stall No", "Stall Size", "Area"]);

// 4-directional only: a diagonal step could slip through the corner gap where
// two stalls touch.
const NAV_DIRS = [
  { dx: 1, dy: 0 },
  { dx: -1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: 0, dy: -1 },
];

class MinHeap {
  constructor() { this.items = []; this.costs = []; }
  get size() { return this.items.length; }
  push(item, cost) {
    this.items.push(item); this.costs.push(cost);
    let i = this.items.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.costs[p] <= this.costs[i]) break;
      this.swap(p, i); i = p;
    }
  }
  pop() {
    const top = this.items[0];
    const li = this.items.pop();
    const lc = this.costs.pop();
    if (this.items.length > 0) {
      this.items[0] = li; this.costs[0] = lc;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let s = i;
        if (l < this.items.length && this.costs[l] < this.costs[s]) s = l;
        if (r < this.items.length && this.costs[r] < this.costs[s]) s = r;
        if (s === i) break;
        this.swap(s, i); i = s;
      }
    }
    return top;
  }
  swap(a, b) {
    const ti = this.items[a]; this.items[a] = this.items[b]; this.items[b] = ti;
    const tc = this.costs[a]; this.costs[a] = this.costs[b]; this.costs[b] = tc;
  }
}

const matMul = (m, n) => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
});

const IDENTITY = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

// Only the forms this exporter emits, plus the common shorthands.
const parseTransformAttr = (str) => {
  if (!str) return IDENTITY;
  let out = IDENTITY;
  const re = /(matrix|translate|scale)\(([^)]*)\)/g;
  let m;
  let matched = false;
  while ((m = re.exec(str)) !== null) {
    const v = m[2].trim().split(/[\s,]+/).map(Number);
    if (v.some(Number.isNaN)) return null;
    let next = null;
    if (m[1] === "matrix" && v.length >= 6) {
      next = { a: v[0], b: v[1], c: v[2], d: v[3], e: v[4], f: v[5] };
    } else if (m[1] === "translate") {
      next = { a: 1, b: 0, c: 0, d: 1, e: v[0] || 0, f: v.length > 1 ? v[1] : 0 };
    } else if (m[1] === "scale") {
      next = { a: v[0] || 1, b: 0, c: 0, d: v.length > 1 ? v[1] : (v[0] || 1), e: 0, f: 0 };
    }
    if (!next) return null;
    out = matMul(out, next);
    matched = true;
  }
  return matched ? out : (str.trim() ? null : IDENTITY);
};

// Straight-line vertices of a path. Curves are only used for glyphs and arrow
// heads in this drawing, so treating their endpoints as vertices is enough.
const pathSegments = (d) => {
  const cmds = d.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!cmds) return [];
  const segs = [];
  let cx = 0, cy = 0, sx = 0, sy = 0, prev = null;
  const emit = (x, y) => { if (prev) segs.push([prev.x, prev.y, x, y]); prev = { x, y }; };
  for (const c of cmds) {
    const op = c[0];
    const a = c.slice(1).match(/-?\d*\.?\d+(?:e-?\d+)?/gi);
    const n = a ? a.map(Number) : [];
    if (op === "M" || op === "m") {
      for (let i = 0; i + 1 < n.length; i += 2) {
        if (op === "M") { cx = n[i]; cy = n[i + 1]; } else { cx += n[i]; cy += n[i + 1]; }
        if (i === 0) { prev = null; sx = cx; sy = cy; }
        emit(cx, cy);
      }
    } else if (op === "L" || op === "l") {
      for (let i = 0; i + 1 < n.length; i += 2) {
        if (op === "L") { cx = n[i]; cy = n[i + 1]; } else { cx += n[i]; cy += n[i + 1]; }
        emit(cx, cy);
      }
    } else if (op === "H" || op === "h") {
      for (const v of n) { cx = op === "H" ? v : cx + v; emit(cx, cy); }
    } else if (op === "V" || op === "v") {
      for (const v of n) { cy = op === "V" ? v : cy + v; emit(cx, cy); }
    } else if (op === "Z" || op === "z") {
      emit(sx, sy);
    } else {
      prev = null; // curve: break the run rather than guess at its shape
    }
  }
  return segs;
};

const inTextLayer = (el) => {
  for (let p = el.parentNode; p && p.getAttribute; p = p.parentNode) {
    const label = p.getAttribute("inkscape:label");
    if (label && TEXT_LAYERS.has(label)) return true;
    if (p.tagName && p.tagName.toLowerCase() === "svg") break;
  }
  return false;
};

// Rasterise the drawing's line work into a blocked-cell grid.
const buildNavGrid = (svgElement, viewBox) => {
  const { x: ox, y: oy, width: vw, height: vh } = viewBox;
  const cols = Math.max(1, Math.ceil(vw / NAV_CELL));
  const rows = Math.max(1, Math.ceil(vh / NAV_CELL));
  const blocked = new Uint8Array(cols * rows);

  const ctmCache = new Map();
  const ctmOf = (el) => {
    // Matrix(path) = Matrix(parent) x transform(path), all in root USER space -
    // see matrixToSvgUserSpace: getCTM() would return viewport pixels once the
    // svg scales to its container, which would misplace every wall. Caching per
    // parent means a handful of matrix reads instead of one per path.
    const parent = el.parentNode;
    let base = ctmCache.get(parent);
    if (base === undefined) {
      base = parent ? matrixToSvgUserSpace(parent) : null;
      ctmCache.set(parent, base);
    }
    const own = parseTransformAttr(el.getAttribute("transform"));
    if (own === null) {
      return matrixToSvgUserSpace(el);
    }
    return base ? matMul(base, own) : own;
  };

  let paths = [];
  try {
    paths = Array.from(svgElement.querySelectorAll("path"));
  } catch (e) {
    paths = [];
  }

  const mark = (x0, y0, x1, y1) => {
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (NAV_CELL * 0.4)) + 1;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const c = Math.floor((x0 + (x1 - x0) * t - ox) / NAV_CELL);
      const r = Math.floor((y0 + (y1 - y0) * t - oy) / NAV_CELL);
      if (c >= 0 && r >= 0 && c < cols && r < rows) blocked[r * cols + c] = 1;
    }
  };

  paths.forEach((el) => {
    if (isOverlayNode(el) || inTextLayer(el)) return;
    const d = el.getAttribute("d");
    if (!d) return;
    const m = ctmOf(el);
    pathSegments(d).forEach(([x0, y0, x1, y1]) => {
      let p0x = x0, p0y = y0, p1x = x1, p1y = y1;
      if (m) {
        p0x = m.a * x0 + m.c * y0 + m.e; p0y = m.b * x0 + m.d * y0 + m.f;
        p1x = m.a * x1 + m.c * y1 + m.e; p1y = m.b * x1 + m.d * y1 + m.f;
      }
      if (Math.hypot(p1x - p0x, p1y - p0y) < MIN_INK_SEG) return;
      mark(p0x, p0y, p1x, p1y);
    });
  });

  // Flood fill the free space. Stalls are sealed pockets; the biggest region
  // left over is the aisle network, and routing never leaves it.
  const comp = new Int32Array(cols * rows).fill(-1);
  const sizes = [];
  const stack = [];
  for (let seed = 0; seed < cols * rows; seed++) {
    if (blocked[seed] || comp[seed] !== -1) continue;
    const id = sizes.length;
    let size = 0;
    stack.length = 0;
    stack.push(seed);
    comp[seed] = id;
    while (stack.length) {
      const cur = stack.pop();
      size++;
      const r = (cur / cols) | 0;
      const c = cur - r * cols;
      if (c + 1 < cols && !blocked[cur + 1] && comp[cur + 1] === -1) { comp[cur + 1] = id; stack.push(cur + 1); }
      if (c - 1 >= 0 && !blocked[cur - 1] && comp[cur - 1] === -1) { comp[cur - 1] = id; stack.push(cur - 1); }
      if (r + 1 < rows && !blocked[cur + cols] && comp[cur + cols] === -1) { comp[cur + cols] = id; stack.push(cur + cols); }
      if (r - 1 >= 0 && !blocked[cur - cols] && comp[cur - cols] === -1) { comp[cur - cols] = id; stack.push(cur - cols); }
    }
    sizes.push(size);
  }
  let main = -1;
  let best = -1;
  sizes.forEach((s, i) => { if (s > best) { best = s; main = i; } });

  // Cells adjacent to a wall cost more, so routes run down aisle centres.
  const hugging = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      if (comp[i] !== main) continue;
      for (let d = 0; d < NAV_DIRS.length && !hugging[i]; d++) {
        const nr = r + NAV_DIRS[d].dy;
        const nc = c + NAV_DIRS[d].dx;
        if (nr < 0 || nc < 0 || nr >= rows || nc >= cols || comp[nr * cols + nc] !== main) hugging[i] = 1;
      }
    }
  }

  return { cols, rows, blocked, comp, main, hugging, ox, oy, aisleCells: best };
};

// A booth centre sits inside its own sealed stall, so step out to the closest
// cell that is actually on the aisle network - the stall's opening.
const nearestAisleCell = (grid, x, y, maxRadius = 60) => {
  const { cols, rows, comp, main } = grid;
  const col0 = Math.floor((x - grid.ox) / NAV_CELL);
  const row0 = Math.floor((y - grid.oy) / NAV_CELL);
  for (let radius = 0; radius <= maxRadius; radius++) {
    let best = null;
    let bestD = Infinity;
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (radius > 0 && Math.max(Math.abs(dr), Math.abs(dc)) !== radius) continue;
        const c = col0 + dc;
        const r = row0 + dr;
        if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
        if (comp[r * cols + c] !== main) continue;
        const d = dr * dr + dc * dc;
        if (d < bestD) { bestD = d; best = { col: c, row: r }; }
      }
    }
    if (best) return best;
  }
  return null;
};

// Dijkstra over (cell, heading), confined to the aisle network. Heading is part
// of the state so a turn can be charged for.
const dijkstraOnGrid = (grid, start, goal) => {
  const { cols, rows, comp, main, hugging } = grid;
  const stateCount = cols * rows * 4;

  const dist = new Float64Array(stateCount).fill(Infinity);
  const prev = new Int32Array(stateCount).fill(-1);
  const settled = new Uint8Array(stateCount);

  const startCell = start.row * cols + start.col;
  const goalCell = goal.row * cols + goal.col;
  if (comp[startCell] !== main || comp[goalCell] !== main) return null;

  const heap = new MinHeap();
  for (let d = 0; d < 4; d++) {
    dist[startCell * 4 + d] = 0;
    heap.push(startCell * 4 + d, 0);
  }

  let goalState = -1;
  while (heap.size > 0) {
    const state = heap.pop();
    if (settled[state]) continue;
    settled[state] = 1;

    const cell = state >> 2;
    const dir = state & 3;
    if (cell === goalCell) { goalState = state; break; }

    const row = (cell / cols) | 0;
    const col = cell - row * cols;

    for (let nd = 0; nd < 4; nd++) {
      const nc = col + NAV_DIRS[nd].dx;
      const nr = row + NAV_DIRS[nd].dy;
      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
      const ncell = nr * cols + nc;
      if (comp[ncell] !== main) continue;

      const nstate = ncell * 4 + nd;
      if (settled[nstate]) continue;

      const step = 1 + (hugging[ncell] ? HUG_COST : 0) + (nd === dir ? 0 : TURN_COST);
      const alt = dist[state] + step;
      if (alt < dist[nstate]) {
        dist[nstate] = alt;
        prev[nstate] = state;
        heap.push(nstate, alt);
      }
    }
  }

  if (goalState < 0) return null;

  const cells = [];
  for (let s = goalState; s !== -1; s = prev[s]) {
    const cell = s >> 2;
    if (cells.length === 0 || cells[cells.length - 1] !== cell) cells.push(cell);
    if (prev[s] === -1) break;
  }
  cells.reverse();
  return cells.map((cell) => {
    const row = (cell / cols) | 0;
    return { col: cell - row * cols, row };
  });
};

// Collapse collinear runs so the drawn polyline has corners, not 400 points.
const simplifyCells = (cells) => {
  if (!cells || cells.length < 3) return cells || [];
  const out = [cells[0]];
  for (let i = 1; i < cells.length - 1; i++) {
    const a = out[out.length - 1];
    const b = cells[i];
    const c = cells[i + 1];
    const straight = (b.col - a.col) * (c.row - b.row) === (b.row - a.row) * (c.col - b.col);
    if (!straight) out.push(b);
  }
  out.push(cells[cells.length - 1]);
  return out;
};

const cellToPoint = (grid, cell) => ({
  x: grid.ox + (cell.col + 0.5) * NAV_CELL,
  y: grid.oy + (cell.row + 0.5) * NAV_CELL,
});

const SvgCanvas = ({
  layout,
  filterHallId,
  filterBoothId,
  onFilterHallChange,
  onFilterBoothChange,
  onSvgDataExtracted,
  zones = [],
  selectedZoneId = "",
  onZoneChange,
  isZoneLoading = false,
  zoneStalls = [],
  statusCounts = {},
  isStatusLoading = false,
  trackerRows = [],
  taskRows = [],
  exhibitorRows = [],
  isTaskLoading = false,
  services = [],
  selectedServiceId = "",
  onServiceChange,
  // Verification state per deliverable, keyed by ExhVendorPlanDtlID. Only the
  // Organizer can move it, the same rule the vendor task screen applies
  verificationById = {},
  canVerify = false,
  verifyingTaskId = null,
  onVerifyTask,
  onOpenTaskDetail,
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipContentRef = useRef(null);
  const tooltipDotRef = useRef(null);

  // Route state
  const [routeFromBoothId, setRouteFromBoothId] = useState("");
  const [routeToBoothId, setRouteToBoothId] = useState("");
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [routeError, setRouteError] = useState("");

  const boothNodesRef = useRef({});
  const navGridRef = useRef(null);
  const nodeBoothMapRef = useRef({});
  const clipIndexRef = useRef({ elementsByClip: new Map(), clipPathDefsById: new Map(), elementsByBoothName: new Map(), textElementsByContent: new Map() });

  const transform = useRef({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    isDragging: false,
    hasDragged: false,
    dragStart: { x: 0, y: 0 },
    dragInitial: { x: 0, y: 0 }
  });

  const zoomLabelRef = useRef(null);
  const willChangeTimeoutRef = useRef(null);
  // Bumped once the booth dots exist in the DOM, so the effect that colours
  // them for the service filter can run after they are created rather than
  // racing the parse.
  const [dotsVersion, setDotsVersion] = useState(0);
  const [isProcessingFilter, setIsProcessingFilter] = useState(false);
  const [filterMessage, setFilterMessage] = useState("");
  const [focusError, setFocusError] = useState("");

  // Halls/booths read out of the drawing. These drive the dropdowns.
  const [svgData, setSvgData] = useState({ halls: [], booths: [] });
  const boothElementsRef = useRef(new Map());

  // Size the canvas to the drawing's own aspect ratio. Letterboxing a 1.29:1
  // plan inside a 3.3:1 box can only ever fill ~39% of it; matching the box to
  // the drawing means the map itself has no dead space at all.
  const canvasWrapRef = useRef(null);
  const [svgAspect, setSvgAspect] = useState(null);
  const [canvasBox, setCanvasBox] = useState({ width: null, height: 500 });

  // Once the user pans or zooms, stop re-imposing the default view on them.
  const hasUserAdjustedRef = useRef(false);
  const defaultViewAppliedRef = useRef(false);

  // API status keyed by booth code. The join must ignore punctuation: the
  // drawing writes "H1-A1" while the database writes "H1A1". Comparing the raw
  // strings matches nothing; stripping non-alphanumerics matches 146/150.
  const statusByBoothName = useMemo(() => {
    const map = new Map();
    if (layout && Array.isArray(layout.booths)) {
      layout.booths.forEach(b => {
        if (b.BoothName) map.set(normalizeBoothKey(b.BoothName), b);
      });
    }
    return map;
  }, [layout]);

  // Booths as shown: SVG geometry + the API's status/vendor detail.
  const booths = useMemo(() => svgData.booths.map(b => {
    const api = statusByBoothName.get(normalizeBoothKey(b.BoothName));
    return api ? {
      ...b,
      StallId: api.BoothID != null ? api.BoothID : api.StallId,
      Status: api.Status,
      StatusColor: api.StatusColor,
      VendorName: api.VendorName,
      CompanyName: api.CompanyName,
      MobileNo: api.MobileNo,
      EmailID: api.EmailID,
    } : b;
  }), [svgData.booths, statusByBoothName]);

  const halls = svgData.halls;

  // The service filter's map projection. `getHallViewAuditTrackerList` reports
  // per hall + service, never per booth, so the finest truthful granularity is
  // "this hall's status for the selected service" — shared by every booth in it.
  // null means no service filter, i.e. leave the map alone.
  const serviceStatusByHall = useMemo(() => {
    if (!selectedServiceId) return null;

    // The rows arrive already filtered server-side. Narrow again only if the
    // ids actually line up; otherwise trust the server rather than dim the
    // whole drawing on an id-naming mismatch.
    const wanted = String(selectedServiceId);
    const matching = (trackerRows || []).filter(
      r => String(r.ServiceID) === wanted || String(r.MainServiceID) === wanted
    );
    const rows = matching.length > 0 ? matching : (trackerRows || []);

    const map = new Map();
    rows.forEach(r => {
      const key = hallKeyOf(r.HallName);
      if (!key) return;
      // Fall back to a truthy status so a hall that carries the service but
      // has no status yet still reads as covered rather than filtered out.
      map.set(key, worstStatus(map.get(key), r.HallStatus || 'Unallocated'));
    });
    return map;
  }, [selectedServiceId, trackerRows]);

  // What a booth should read as right now: the booth's own layout status when
  // no service is selected, otherwise the selected service's status for its
  // hall — or `dimmed` when that hall carries no row for the service at all.
  const boothDisplayStatus = useCallback((booth) => {
    if (!booth) return { status: null, dimmed: false };
    if (!serviceStatusByHall) return { status: booth.Status || null, dimmed: false };
    const status = serviceStatusByHall.get(hallKeyOf(booth.HallName));
    return status ? { status, dimmed: false } : { status: null, dimmed: true };
  }, [serviceStatusByHall]);

  // Read by the SVG event handlers, which are attached once per layout and so
  // would otherwise close over a stale filter.
  const boothDisplayStatusRef = useRef(boothDisplayStatus);
  useEffect(() => {
    boothDisplayStatusRef.current = boothDisplayStatus;
  }, [boothDisplayStatus]);

  // sp_get_vendorauditTracker leaves `name` null on some rows even though
  // ServiceID is set, which showed the task card titled with the literal word
  // "Service". The service dropdown already carries the id -> name mapping, so
  // resolve through it rather than printing a placeholder.
  const serviceNameById = useMemo(() => {
    const map = new Map();
    (services || []).forEach((svc) => {
      if (svc && svc.ServiceID != null) {
        map.set(String(svc.ServiceID), svc.Name || svc.DisplayName);
      }
    });
    return map;
  }, [services]);

  const serviceNameOf = (row) =>
    row.name ||
    (row.ServiceID != null ? serviceNameById.get(String(row.ServiceID)) : null) ||
    null;

  // Stall codes belonging to the selected zone. null means "no zone filter".
  const zoneBoothKeys = useMemo(() => {
    if (!selectedZoneId) return null;
    const set = new Set();
    (zoneStalls || []).forEach(s => {
      if (String(s.HallZoneMappingID) !== String(selectedZoneId)) return;
      if (s.StallName) set.add(normalizeBoothKey(s.StallName));
    });
    return set;
  }, [zoneStalls, selectedZoneId]);

  const boothMap = useMemo(() => {
    const map = {};
    booths.forEach(b => { map[b.BoothName] = b; });
    return map;
  }, [booths]);

  // Keep the plan covering the viewport: pin the offsets so an edge can never
  // be dragged inward to reveal background, and centre an axis that genuinely
  // is smaller than the box.
  const clampTransform = useCallback(() => {
    const container = containerRef.current;
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    if (!container || !svgElement) return;

    const bounds = container.getBoundingClientRect();
    const rectW = bounds.width;
    const rectH = bounds.height;
    if (!rectW || !rectH) return;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vbW = (vb && vb.width) || 792;
    const vbH = (vb && vb.height) || 612;

    const pxPerUnit = Math.min(rectW / vbW, rectH / vbH);
    const padX = (rectW - vbW * pxPerUnit) / 2;
    const padY = (rectH - vbH * pxPerUnit) / 2;

    const { scale } = transform.current;
    const drawnW = vbW * pxPerUnit * scale;
    const drawnH = vbH * pxPerUnit * scale;

    const clampAxis = (offset, pad, drawn, viewport) => {
      const lead = pad * scale;
      if (drawn <= viewport) return (viewport - drawn) / 2 - lead;
      return Math.min(-lead, Math.max(viewport - drawn - lead, offset));
    };

    transform.current.offsetX = clampAxis(transform.current.offsetX, padX, drawnW, rectW);
    transform.current.offsetY = clampAxis(transform.current.offsetY, padY, drawnH, rectH);
  }, []);

  const updateTransformStyle = useCallback((animate = false) => {
    if (!svgRef.current) return;
    clampTransform();
    const { scale, offsetX, offsetY } = transform.current;

    svgRef.current.style.willChange = "transform";
    if (willChangeTimeoutRef.current) clearTimeout(willChangeTimeoutRef.current);
    willChangeTimeoutRef.current = setTimeout(() => {
      if (svgRef.current) svgRef.current.style.willChange = "auto";
    }, animate ? 400 : 200);

    if (animate) {
      svgRef.current.style.transition = "transform 0.35s ease-in-out";
      setTimeout(() => {
        if (svgRef.current) svgRef.current.style.transition = "none";
      }, 350);
    } else {
      svgRef.current.style.transition = "none";
    }

    svgRef.current.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;

    if (zoomLabelRef.current) {
      zoomLabelRef.current.innerText = `${Math.round(scale * 100)}%`;
    }
  }, [clampTransform]);

  // Focus box for a booth code, in SVG root user space. Multiple occurrences of
  // one code are unioned, unless that union is implausibly large (the same code
  // printed in two unrelated places), in which case the first wins.
  // Unpadded label extent. This is what routing treats as the booth footprint.
  const getBoothRawBox = useCallback((boothName) => {
    const els = boothElementsRef.current.get(boothName) || [];
    if (els.length === 0) return null;
    const boxes = els.map(elementBoxInSvgSpace).filter(Boolean);
    if (boxes.length === 0) return null;

    const merged = unionBoxes(boxes);
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    const vw = (svgElement && svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.width) || 792;
    const vh = (svgElement && svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.height) || 612;
    if (merged && (merged.width > vw * 0.25 || merged.height > vh * 0.25)) {
      return boxes[0];
    }
    return merged;
  }, []);

  // Padded box for the focus ring and hit target - deliberately generous, and
  // deliberately NOT what the nav grid uses.
  const getBoothBox = useCallback((boothName) => padBox(getBoothRawBox(boothName)), [getBoothRawBox]);

  // Projects a point from SVG user space into "world" space: container pixels
  // as they would be at scale 1 / zero offset. getScreenCTM() already folds in
  // the wrapper's CSS transform, so we divide it back out.
  const svgPointToWorld = useCallback((x, y, ctm, containerRect) => {
    const { scale, offsetX, offsetY } = transform.current;
    const sx = ctm ? ctm.a * x + ctm.c * y + ctm.e : x;
    const sy = ctm ? ctm.b * x + ctm.d * y + ctm.f : y;
    return {
      x: (sx - containerRect.left - offsetX) / scale,
      y: (sy - containerRect.top - offsetY) / scale,
    };
  }, []);

  // Frames an SVG-user-space box in the viewport.
  const zoomToSvgBox = useCallback((box) => {
    if (!box || !svgRef.current || !containerRef.current) return false;
    const svgElement = svgRef.current.querySelector("svg");
    if (!svgElement) return false;

    const containerRect = containerRef.current.getBoundingClientRect();
    if (!containerRect.width || !containerRect.height) return false;

    const ctm = typeof svgElement.getScreenCTM === "function" ? svgElement.getScreenCTM() : null;
    const a = svgPointToWorld(box.x, box.y, ctm, containerRect);
    const b = svgPointToWorld(box.x + box.width, box.y + box.height, ctm, containerRect);

    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);

    const width = Math.max(maxX - minX, 1);
    const height = Math.max(maxY - minY, 1);

    const padding = 0.12;
    // No cover floor here: a hall is tall and narrow, and forcing it up to the
    // fit-width scale would frame only part of it. clampTransform still stops
    // the view drifting off the plan.
    const newScale = Math.max(0.4, Math.min(8, Math.min(
      (containerRect.width * (1 - padding * 2)) / width,
      (containerRect.height * (1 - padding * 2)) / height
    )));

    transform.current.scale = newScale;
    transform.current.offsetX = containerRect.width / 2 - ((minX + maxX) / 2) * newScale;
    transform.current.offsetY = containerRect.height / 2 - ((minY + maxY) / 2) * newScale;

    updateTransformStyle(true);
    return true;
  }, [svgPointToWorld, updateTransformStyle]);

  // Spotlight overlay. Takes a LIST of boxes and punches one hole per booth, so
  // a zone lights up only its own stalls - a single hole over the zone's bounding
  // box would leave every unrelated booth inside that rectangle lit too.
  const drawFocusOverlay = useCallback((boxes, label) => {
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    if (!svgElement) return;

    let g = svgElement.querySelector("g#booth-focus");
    if (!g) {
      g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.setAttribute("id", "booth-focus");
      g.setAttribute("pointer-events", "none");
    }
    // Always re-append so the overlay stays on top of later-added groups.
    svgElement.appendChild(g);
    g.innerHTML = "";

    const list = (Array.isArray(boxes) ? boxes : [boxes]).filter(Boolean);
    if (list.length === 0) return;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vx = (vb && vb.x) || 0;
    const vy = (vb && vb.y) || 0;
    const vw = (vb && vb.width) || 792;
    const vh = (vb && vb.height) || 612;

    const svgNs = "http://www.w3.org/2000/svg";
    const maskId = "booth-focus-mask";

    const mask = document.createElementNS(svgNs, "mask");
    mask.setAttribute("id", maskId);
    const maskShow = document.createElementNS(svgNs, "rect");
    maskShow.setAttribute("x", vx); maskShow.setAttribute("y", vy);
    maskShow.setAttribute("width", vw); maskShow.setAttribute("height", vh);
    maskShow.setAttribute("fill", "#fff");
    mask.appendChild(maskShow);
    list.forEach((box) => {
      const hole = document.createElementNS(svgNs, "rect");
      hole.setAttribute("x", box.x); hole.setAttribute("y", box.y);
      hole.setAttribute("width", box.width); hole.setAttribute("height", box.height);
      hole.setAttribute("rx", "2");
      hole.setAttribute("fill", "#000");
      mask.appendChild(hole);
    });
    g.appendChild(mask);

    const dim = document.createElementNS(svgNs, "rect");
    dim.setAttribute("x", vx); dim.setAttribute("y", vy);
    dim.setAttribute("width", vw); dim.setAttribute("height", vh);
    dim.setAttribute("fill", "#0f172a");
    dim.setAttribute("opacity", "0.55");
    dim.setAttribute("mask", `url(#${maskId})`);
    g.appendChild(dim);

    // Pulse a lone booth; a pulsing ring on 40 stalls is just noise.
    const single = list.length === 1;
    list.forEach((box) => {
      const ring = document.createElementNS(svgNs, "rect");
      ring.setAttribute("x", box.x); ring.setAttribute("y", box.y);
      ring.setAttribute("width", box.width); ring.setAttribute("height", box.height);
      ring.setAttribute("rx", "2");
      ring.setAttribute("fill", "none");
      ring.setAttribute("stroke", "#4f46e5");
      ring.setAttribute("stroke-width", single ? "1.5" : "0.9");
      if (single) ring.setAttribute("class", "booth-focus-ring");
      g.appendChild(ring);
    });

    if (label) {
      const union = unionBoxes(list);
      const text = document.createElementNS(svgNs, "text");
      text.setAttribute("x", union.x + union.width / 2);
      text.setAttribute("y", union.y - 3);
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("font-size", "7");
      text.setAttribute("font-weight", "bold");
      text.setAttribute("fill", "#4f46e5");
      text.setAttribute("data-overlay", "1");
      text.textContent = label;
      g.appendChild(text);
    }
  }, []);

  // Opening view: the plan's full width, so every hall is visible left to
  // right. It overflows vertically at that scale, which the user pans through.
  const applyDefaultView = useCallback((animate = false) => {
    if (!svgRef.current || !containerRef.current) return false;
    const svgElement = svgRef.current.querySelector("svg");
    if (!svgElement) return false;

    const rect = containerRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vbX = (vb && vb.x) || 0;
    const vbY = (vb && vb.y) || 0;
    const vbW = (vb && vb.width) || 792;
    const vbH = (vb && vb.height) || 612;

    // preserveAspectRatio="xMidYMid meet": uniform scale, centred, so this is
    // the px-per-unit and padding the SVG viewport itself applies at scale 1.
    const pxPerUnit = Math.min(rect.width / vbW, rect.height / vbH);
    const padX = (rect.width - vbW * pxPerUnit) / 2;
    const padY = (rect.height - vbH * pxPerUnit) / 2;

    // Fit the plan's full width, so every hall is visible left to right. The
    // drawing is taller than the box at this scale, which is intended - the
    // overflow is panned vertically.
    const scale = Math.max(1, rect.width / (vbW * pxPerUnit));

    // Keep the plan's centre in the middle of the viewport.
    const centreBaseX = (vbX + vbW / 2 - vbX) * pxPerUnit + padX;
    const centreBaseY = (vbY + vbH / 2 - vbY) * pxPerUnit + padY;

    transform.current.scale = scale;
    transform.current.offsetX = rect.width / 2 - centreBaseX * scale;
    transform.current.offsetY = rect.height / 2 - centreBaseY * scale;
    updateTransformStyle(animate);
    return true;
  }, [updateTransformStyle]);

  // Keep the canvas box locked to the drawing's aspect ratio, capped so a tall
  // plan can't push the rest of the page off screen.
  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;

    const measure = () => {
      const available = wrap.clientWidth;
      if (!available) return;
      // Full width always. Height is capped at the plan's own height once its
      // width fits, so a narrow screen can never letterbox top and bottom.
      const aspect = svgAspect || 792 / 612;
      const height = Math.max(
        300,
        Math.min(Math.round(window.innerHeight * 0.72), Math.round(available / aspect))
      );
      setCanvasBox((prev) => (prev.width === available && prev.height === height ? prev : { width: available, height }));
    };

    measure();

    if (typeof window.ResizeObserver !== "function") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new window.ResizeObserver(measure);
    ro.observe(wrap);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [svgAspect]);

  // Apply the opening view once the drawing has been measured, and again if the
  // box is resized - but never after the user has panned or zoomed themselves.
  useEffect(() => {
    if (hasUserAdjustedRef.current) return;
    if (filterHallId || filterBoothId) return;
    if (booths.length === 0) return;

    const timer = setTimeout(() => {
      if (hasUserAdjustedRef.current) return;
      if (applyDefaultView(defaultViewAppliedRef.current)) {
        defaultViewAppliedRef.current = true;
      }
    }, 40);
    return () => clearTimeout(timer);
  }, [booths, canvasBox, filterHallId, filterBoothId, applyDefaultView]);

  // A new drawing gets a fresh opening view.
  useEffect(() => {
    hasUserAdjustedRef.current = false;
    defaultViewAppliedRef.current = false;
  }, [layout.svgContent]);

  useEffect(() => {
    updateTransformStyle();
  }, [updateTransformStyle]);

  const zoomAtPoint = useCallback((newScale, pointX, pointY) => {
    const clampedScale = Math.max(0.4, Math.min(8, newScale));
    const { scale, offsetX, offsetY } = transform.current;
    if (clampedScale === scale) return;

    const worldX = (pointX - offsetX) / scale;
    const worldY = (pointY - offsetY) / scale;

    transform.current.scale = clampedScale;
    transform.current.offsetX = pointX - worldX * clampedScale;
    transform.current.offsetY = pointY - worldY * clampedScale;
  }, []);


  // ─── Build index, paint status colours, wire interaction ──────────────
  // Keyed to the SVG itself: a filter change must never rebuild the graph.
  // ─── Read halls & booths out of the drawing ───────────────────────────
  // Runs once per SVG. Everything downstream keys off what this finds.
  useEffect(() => {
    if (!svgRef.current || !layout.svgContent) return;

    const svgContainer = svgRef.current;
    const timer = setTimeout(() => {
      const svgElement = svgContainer.querySelector('svg');
      if (!svgElement) return;

      clipIndexRef.current = buildSvgIndex(svgElement);

      // The exported SVG carries width="792" height="612", so it renders at its
      // intrinsic size and sits marooned in a wider container. Let it scale to
      // whatever box we give it instead. All our geometry goes through
      // getScreenCTM / getCTM, so this does not disturb focus or routing.
      svgElement.setAttribute("width", "100%");
      svgElement.setAttribute("height", "100%");
      const vbox = svgElement.viewBox && svgElement.viewBox.baseVal;
      if (vbox && vbox.width > 0 && vbox.height > 0) {
        setSvgAspect(vbox.width / vbox.height);
      }
      if (!svgElement.getAttribute("preserveAspectRatio")) {
        svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");
      }

      const { halls: svgHalls, booths: svgBooths, elementsByBooth } = extractSvgLayoutData(svgElement);
      boothElementsRef.current = elementsByBooth;
      setSvgData({ halls: svgHalls, booths: svgBooths });
      if (onSvgDataExtracted) onSvgDataExtracted({ halls: svgHalls, booths: svgBooths });
    }, 60);

    return () => clearTimeout(timer);
    // onSvgDataExtracted intentionally omitted: parents commonly pass an inline
    // callback, which would re-run the parse on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.svgContent]);

  useEffect(() => {
    if (!svgRef.current || !layout.svgContent) return;

    const svgContainer = svgRef.current;
    let cleanupListeners = null;

    const timer = setTimeout(() => {
      const svgElement = svgContainer.querySelector('svg');
      if (!svgElement) return;

      // Color injection (fast)
      let styleTag = svgElement.querySelector('style[data-booth-colors]');
      if (!styleTag) {
        styleTag = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        styleTag.setAttribute('data-booth-colors', 'true');
        styleTag.setAttribute('type', 'text/css');
        svgElement.insertBefore(styleTag, svgElement.firstChild);
      }

      let cssRules = '';
      const clipDefs = clipIndexRef.current.clipPathDefsById;
      const vbW = (svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.width) || 792;
      const vbH = (svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.height) || 612;
      (layout.booths || []).forEach(booth => {
        const clipId = booth.ClipId;
        if (!clipId || !String(clipId).startsWith('clip_') || !booth.StatusColor) return;
        // Only paint a clip that really exists and is booth-sized. The API ships
        // sequential ClipIds that mostly aren't in the file, and the few that
        // are turn out to be full-canvas layer clips - painting those floods
        // the whole drawing with one status colour.
        const def = clipDefs.get(normalizeId(clipId));
        if (!def) return;
        let box = null;
        try {
          box = unionBoxes(Array.from(def.querySelectorAll('path, rect')).map(elementBoxInSvgSpace));
        } catch (e) { box = null; }
        if (!box || box.width > vbW * 0.5 || box.height > vbH * 0.5) return;
        cssRules += `[clip-path="url(#${clipId})"] { fill: ${booth.StatusColor} !important; stroke: ${booth.StatusColor} !important; cursor: pointer; }\n`;
      });

      // Booth label nodes are fill-opacity:0, so they are never "painted" and
      // the default visiblePainted hit-testing ignores them entirely.
      cssRules += `
        [data-booth-name] { pointer-events: all; cursor: pointer; }
        #booth-hits rect { fill: transparent; pointer-events: all; cursor: pointer; }
        #booth-nodes circle.booth-node-dot { cursor: pointer; transition: r 0.15s ease, filter 0.15s ease; }
        #booth-nodes circle.booth-node-dot:hover { r: 4.5; filter: drop-shadow(0 0 3px rgba(79,70,229,0.9)); }
        #booth-focus .booth-focus-ring { animation: boothFocusPulse 1.6s ease-in-out infinite; }
        @keyframes boothFocusPulse {
          0%, 100% { stroke-opacity: 1; stroke-width: 1.5; }
          50%      { stroke-opacity: .45; stroke-width: 2.6; }
        }
      `;
      styleTag.textContent = cssRules;

      // ─── Booth nodes from the label geometry ───
      const nodes = {};
      const nodeBoothMap = {};
      const boothBoxes = {};
      booths.forEach(booth => {
        const raw = getBoothRawBox(booth.BoothName);
        if (!raw) return;
        const box = padBox(raw);
        const id = booth.BoothName;
        nodes[id] = { id, x: box.x + box.width / 2, y: box.y + box.height / 2 };
        nodeBoothMap[id] = booth;
        boothBoxes[id] = box;
      });

      boothNodesRef.current = nodes;
      nodeBoothMapRef.current = nodeBoothMap;

      // Generous transparent hit targets - the label glyphs alone are tiny.
      let hitsGroup = svgElement.querySelector('g#booth-hits');
      if (!hitsGroup) {
        hitsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        hitsGroup.setAttribute('id', 'booth-hits');
        svgElement.appendChild(hitsGroup);
      }
      hitsGroup.innerHTML = '';
      Object.entries(boothBoxes).forEach(([id, box]) => {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', box.x);
        rect.setAttribute('y', box.y);
        rect.setAttribute('width', box.width);
        rect.setAttribute('height', box.height);
        rect.setAttribute('data-booth-name', id);
        hitsGroup.appendChild(rect);
      });

      // Draw dots
      let dotsGroup = svgElement.querySelector('g#booth-nodes');
      if (!dotsGroup) {
        dotsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        dotsGroup.setAttribute('id', 'booth-nodes');
        svgElement.appendChild(dotsGroup);
      }
      dotsGroup.innerHTML = '';

      Object.values(nodes).forEach(node => {
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', node.x);
        circle.setAttribute('cy', node.y);
        circle.setAttribute('r', '2.5');
        // The drawing has no fillable booth shape, so the dot carries status.
        // Painted with the booth's own status here; the service-filter effect
        // below repaints once the dots exist, and owns it from then on.
        paintBoothDot(circle, nodeBoothMap[node.id] && nodeBoothMap[node.id].Status, false);
        circle.setAttribute('stroke', '#fff');
        circle.setAttribute('stroke-width', '1');
        circle.setAttribute('class', 'booth-node-dot');
        circle.setAttribute('data-node-id', node.id);
        circle.style.pointerEvents = 'all';

        // Node click -> open popup with that node's booth data
        circle.addEventListener('click', (e) => {
          e.stopPropagation();
          if (transform.current.hasDragged) return;
          const booth = nodeBoothMap[node.id];
          if (booth) setSelectedBooth(booth);
        });

        // Node hover -> reuse existing tooltip
        circle.addEventListener('mouseover', () => {
          const booth = nodeBoothMap[node.id];
          if (booth && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current && svgContainer) {
            const rect = circle.getBoundingClientRect();
            const { status, dimmed } = boothDisplayStatusRef.current(booth);
            tooltipContentRef.current.innerText =
              `${booth.BoothName} - ${dimmed ? 'Not in this service' : status || 'Unknown'}`;
            if (status) {
              tooltipDotRef.current.style.display = 'inline-block';
              tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
            } else {
              tooltipDotRef.current.style.display = 'none';
            }
            tooltipRef.current.style.left = `${rect.left}px`;
            tooltipRef.current.style.top = `${rect.top - 10}px`;
            tooltipRef.current.style.display = 'block';
          }
        });
        circle.addEventListener('mouseout', () => {
          if (tooltipRef.current) tooltipRef.current.style.display = 'none';
        });

        dotsGroup.appendChild(circle);
      });
      setDotsVersion(v => v + 1);

      // Rasterise the hall once per layout. Booth footprints become walls; what
      // survives is the aisle network every route is confined to.
      const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
      const viewBox = {
        x: (vb && vb.x) || 0,
        y: (vb && vb.y) || 0,
        width: (vb && vb.width) || 792,
        height: (vb && vb.height) || 612,
      };
      navGridRef.current = buildNavGrid(svgElement, viewBox);

      let routeGroup = svgElement.querySelector('g#route-path');
      if (!routeGroup) {
        routeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        routeGroup.setAttribute('id', 'route-path');
        svgElement.appendChild(routeGroup);
      }

      // Event Delegation for hover
      const handleMouseOver = (e) => {
        const target = e.target.closest('[data-booth-name]');
        if (target && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current) {
          const boothName = target.getAttribute('data-booth-name');
          if (boothName && boothMap[boothName]) {
            const booth = boothMap[boothName];
            const rect = target.getBoundingClientRect();

            const { status, dimmed } = boothDisplayStatusRef.current(booth);
            tooltipContentRef.current.innerText =
              `${booth.BoothName} - ${dimmed ? 'Not in this service' : status || 'Unknown'}`;

            if (status) {
              tooltipDotRef.current.style.display = 'inline-block';
              tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
            } else {
              tooltipDotRef.current.style.display = 'none';
            }

            tooltipRef.current.style.left = `${rect.left}px`;
            tooltipRef.current.style.top = `${rect.top - 10}px`;
            tooltipRef.current.style.display = 'block';
          }
        }
      };

      const handleMouseOut = (e) => {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      };

      const handleClick = (e) => {
        if (transform.current.hasDragged) {
          console.log("Click ignored due to dragging");
          return;
        }
        const target = e.target.closest('[data-booth-name]');
        console.log("SVG Clicked! Target found:", target ? target.getAttribute('data-booth-name') : "None");
        if (target) {
          const boothName = target.getAttribute('data-booth-name');
          if (boothName && boothMap[boothName]) {
            console.log("Setting selected booth:", boothMap[boothName]);
            setSelectedBooth(boothMap[boothName]);
          }
        } else {
          setSelectedBooth(null);
        }
      };

      let cursorStyleTag = svgElement.querySelector('style[data-booth-cursor]');
      if (!cursorStyleTag) {
        cursorStyleTag = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        cursorStyleTag.setAttribute('data-booth-cursor', 'true');
        cursorStyleTag.setAttribute('type', 'text/css');
        cursorStyleTag.textContent = `[data-booth-name] { cursor: pointer; }`;
        svgElement.insertBefore(cursorStyleTag, svgElement.firstChild);
      }

      svgContainer.addEventListener('mouseover', handleMouseOver);
      svgContainer.addEventListener('mouseout', handleMouseOut);
      svgContainer.addEventListener('click', handleClick);

      cleanupListeners = () => {
        svgContainer.removeEventListener('mouseover', handleMouseOver);
        svgContainer.removeEventListener('mouseout', handleMouseOut);
        svgContainer.removeEventListener('click', handleClick);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      // Returning this from inside the setTimeout would discard it and leak a
      // fresh set of listeners on every re-run.
      if (cleanupListeners) cleanupListeners();
    };
  }, [layout.svgContent, layout.booths, booths, boothMap, getBoothRawBox]);

  // ─── Service filter -> map ────────────────────────────────────────────
  // Kept out of the parse effect above on purpose: changing the service should
  // repaint dots, not re-rasterise the nav grid (~180 ms per layout).
  useEffect(() => {
    if (!svgRef.current) return;
    const dots = svgRef.current.querySelectorAll('#booth-nodes circle.booth-node-dot');
    if (dots.length === 0) return;

    const nodeBoothMap = nodeBoothMapRef.current || {};
    Array.from(dots).forEach(circle => {
      const booth = nodeBoothMap[circle.getAttribute('data-node-id')];
      const { status, dimmed } = boothDisplayStatus(booth);
      paintBoothDot(circle, status, dimmed);
    });
  }, [boothDisplayStatus, dotsVersion]);

  // ─── Focus + spotlight for the active Hall / Booth selection ──────────
  // One effect owns both the framing and the highlight so they can never
  // disagree about what is selected.
  useEffect(() => {
    if (!svgRef.current) return;
    if (booths.length === 0) return;

    if (!filterHallId && !filterBoothId) {
      drawFocusOverlay(null);
      setFocusError("");
      setIsProcessingFilter(false);
      return;
    }

    let finishTimer = null;
    const timer = setTimeout(() => {
      // One box per booth: the spotlight lights exactly the selected stalls and
      // nothing that merely sits between them.
      let boxes = [];
      let label = "";
      let missing = "";

      if (filterBoothId) {
        const booth = boothMap[filterBoothId];
        label = booth ? booth.BoothName : String(filterBoothId);
        boxes = [getBoothBox(label)].filter(Boolean);
        if (boxes.length === 0) missing = `Booth "${label}"`;
      } else {
        // A zone deliberately has no map representation - it segregates the
        // booth list only, so the map stays framed on the hall until a booth
        // is picked.
        const hallBooths = booths.filter(b => b.HallID === filterHallId);
        const hall = halls.find(h => h.HallID === filterHallId);
        label = hall ? hall.HallName : String(filterHallId);
        boxes = hallBooths.map(b => getBoothBox(b.BoothName)).filter(Boolean);
        if (boxes.length === 0) missing = `Hall "${label}"`;
      }

      drawFocusOverlay(boxes, boxes.length > 0 ? label : "");
      const union = unionBoxes(boxes);
      const zoomed = union ? zoomToSvgBox(union) : false;

      // Failing silently here is what made this look broken - say so instead.
      setFocusError(
        zoomed ? "" : `${missing || label} could not be located in the layout drawing, so the view was not moved.`
      );

      finishTimer = setTimeout(() => setIsProcessingFilter(false), 450);
    }, 120);

    return () => {
      clearTimeout(timer);
      if (finishTimer) clearTimeout(finishTimer);
    };
    // Zone is intentionally absent from these deps: changing it must not
    // re-frame or re-highlight the map.
  }, [filterHallId, filterBoothId, booths, halls, boothMap, getBoothBox, drawFocusOverlay, zoomToSvgBox]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const touchState = {
      isPinching: false,
      isPanning: false,
      startDistance: 0,
      startScale: 1,
      startMidpoint: { x: 0, y: 0 },
      startOffset: { x: 0, y: 0 },
      lastPan: { x: 0, y: 0 },
    };

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const getMidpoint = (touches, rect) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
      y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top,
    });

    const onTouchStart = (e) => {
      const rect = container.getBoundingClientRect();
      if (e.touches.length === 2) {
        e.preventDefault();
        touchState.isPinching = true;
        touchState.isPanning = false;
        touchState.startDistance = getDistance(e.touches) || 1;
        touchState.startScale = transform.current.scale;
        touchState.startMidpoint = getMidpoint(e.touches, rect);
        touchState.startOffset = { x: transform.current.offsetX, y: transform.current.offsetY };
      } else if (e.touches.length === 1) {
        touchState.isPanning = true;
        touchState.isPinching = false;
        transform.current.hasDragged = false;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onTouchMove = (e) => {
      if (touchState.isPinching && e.touches.length === 2) {
        e.preventDefault();
        hasUserAdjustedRef.current = true;
        const rect = container.getBoundingClientRect();
        const newDistance = getDistance(e.touches) || 1;
        const scaleFactor = newDistance / touchState.startDistance;
        const newScale = Math.max(0.4, Math.min(8, touchState.startScale * scaleFactor));

        const worldX = (touchState.startMidpoint.x - touchState.startOffset.x) / touchState.startScale;
        const worldY = (touchState.startMidpoint.y - touchState.startOffset.y) / touchState.startScale;

        transform.current.scale = newScale;
        transform.current.offsetX = touchState.startMidpoint.x - worldX * newScale;
        transform.current.offsetY = touchState.startMidpoint.y - worldY * newScale;
        updateTransformStyle();
      } else if (touchState.isPanning && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - touchState.lastPan.x;
        const dy = e.touches[0].clientY - touchState.lastPan.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          transform.current.hasDragged = true;
          hasUserAdjustedRef.current = true;
        }
        transform.current.offsetX += dx;
        transform.current.offsetY += dy;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        updateTransformStyle();
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length === 0) {
        touchState.isPinching = false;
        touchState.isPanning = false;
      } else if (e.touches.length === 1) {
        touchState.isPinching = false;
        touchState.isPanning = true;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', onTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [updateTransformStyle]);

  // ─── Route Path Rendering ───────────────────────────────────────────
  useEffect(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.querySelector('svg');
    if (!svgElement) return;
    const routeGroup = svgElement.querySelector('g#route-path');
    if (!routeGroup) return;

    routeGroup.innerHTML = '';

    if (!routeFromBoothId || !routeToBoothId) {
      setRouteError("");
      return;
    }

    if (routeFromBoothId === routeToBoothId) {
      setRouteError("");
      return;
    }

    setIsProcessingFilter(true);
    setFilterMessage("Routing optimal path...");
    setRouteError("");

    const timer = setTimeout(() => {
      const startId = String(routeFromBoothId);
      const endId = String(routeToBoothId);

      const startNode = boothNodesRef.current[startId];
      const endNode = boothNodesRef.current[endId];
      const grid = navGridRef.current;

      if (!startNode || !endNode || !grid) {
        setRouteError(
          !endNode
            ? `Destination "${endId}" does not exist on this layout.`
            : !startNode
              ? `Origin "${startId}" does not exist on this layout.`
              : "The walkable area could not be derived from this layout."
        );
        setIsProcessingFilter(false);
        return;
      }

      // Each stall is a sealed pocket, so step out to the nearest cell that is
      // actually on the aisle network - that is the stall's opening.
      const startCell = nearestAisleCell(grid, startNode.x, startNode.y);
      const endCell = nearestAisleCell(grid, endNode.x, endNode.y);

      if (!startCell || !endCell) {
        setRouteError(`No aisle could be found next to "${!startCell ? startId : endId}".`);
        setIsProcessingFilter(false);
        return;
      }

      const cells = dijkstraOnGrid(grid, startCell, endCell);
      if (!cells || cells.length === 0) {
        setRouteError(`No walkable route connects "${startId}" and "${endId}".`);
        setIsProcessingFilter(false);
        return;
      }

      // Anchor the drawn line on the booths themselves, not the aisle mouths.
      const waypoints = simplifyCells(cells).map(c => cellToPoint(grid, c));
      const points = [
        `${startNode.x},${startNode.y}`,
        ...waypoints.map(p => `${p.x},${p.y}`),
        `${endNode.x},${endNode.y}`,
      ].join(' ');

      const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      polyline.setAttribute('points', points);
      polyline.setAttribute('fill', 'none');
      polyline.setAttribute('stroke', '#ef4444'); // red route
      polyline.setAttribute('stroke-width', '2');
      polyline.setAttribute('stroke-dasharray', '6, 5');
      polyline.setAttribute('stroke-linejoin', 'round');
      polyline.setAttribute('stroke-linecap', 'round');
      polyline.setAttribute('pointer-events', 'none');

      const animate = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      animate.textContent = `
        @keyframes dash {
          to { stroke-dashoffset: -16; }
        }
        #route-path polyline {
          animation: dash 1s linear infinite;
        }
        @keyframes routePulse {
          0% { r: 5; opacity: 1; }
          50% { r: 9; opacity: 0.35; }
          100% { r: 5; opacity: 1; }
        }
        #route-path .route-pulse {
          animation: routePulse 1.4s ease-in-out infinite;
          pointer-events: none;
        }
      `;
      routeGroup.appendChild(animate);
      routeGroup.appendChild(polyline);

      // ─── "You are here" marker at the start node ───
      const startPulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      startPulse.setAttribute('cx', startNode.x);
      startPulse.setAttribute('cy', startNode.y);
      startPulse.setAttribute('r', '5');
      startPulse.setAttribute('fill', '#22c55e');
      startPulse.setAttribute('fill-opacity', '0.35');
      startPulse.setAttribute('class', 'route-pulse');
      routeGroup.appendChild(startPulse);

      const startDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      startDot.setAttribute('cx', startNode.x);
      startDot.setAttribute('cy', startNode.y);
      startDot.setAttribute('r', '4');
      startDot.setAttribute('fill', '#22c55e');
      startDot.setAttribute('stroke', '#fff');
      startDot.setAttribute('stroke-width', '1.5');
      startDot.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(startDot);

      const startLabelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      const startLabelText = "You are here";
      startLabelBg.setAttribute('x', startNode.x - 30);
      startLabelBg.setAttribute('y', startNode.y - 22);
      startLabelBg.setAttribute('width', '60');
      startLabelBg.setAttribute('height', '14');
      startLabelBg.setAttribute('rx', '3');
      startLabelBg.setAttribute('fill', '#22c55e');
      startLabelBg.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(startLabelBg);

      const startLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      startLabel.setAttribute('x', startNode.x);
      startLabel.setAttribute('y', startNode.y - 12);
      startLabel.setAttribute('text-anchor', 'middle');
      startLabel.setAttribute('font-size', '7');
      startLabel.setAttribute('fill', '#fff');
      startLabel.setAttribute('font-weight', 'bold');
      startLabel.setAttribute('pointer-events', 'none');
      startLabel.textContent = startLabelText;
      routeGroup.appendChild(startLabel);

      // ─── "Destination" marker at the end node ───
      const endPulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      endPulse.setAttribute('cx', endNode.x);
      endPulse.setAttribute('cy', endNode.y);
      endPulse.setAttribute('r', '5');
      endPulse.setAttribute('fill', '#ef4444');
      endPulse.setAttribute('fill-opacity', '0.35');
      endPulse.setAttribute('class', 'route-pulse');
      routeGroup.appendChild(endPulse);

      const endDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      endDot.setAttribute('cx', endNode.x);
      endDot.setAttribute('cy', endNode.y);
      endDot.setAttribute('r', '4');
      endDot.setAttribute('fill', '#ef4444');
      endDot.setAttribute('stroke', '#fff');
      endDot.setAttribute('stroke-width', '1.5');
      endDot.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(endDot);

      const endLabelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      const endLabelText = "Destination";
      endLabelBg.setAttribute('x', endNode.x - 27);
      endLabelBg.setAttribute('y', endNode.y - 22);
      endLabelBg.setAttribute('width', '54');
      endLabelBg.setAttribute('height', '14');
      endLabelBg.setAttribute('rx', '3');
      endLabelBg.setAttribute('fill', '#ef4444');
      endLabelBg.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(endLabelBg);

      const endLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      endLabel.setAttribute('x', endNode.x);
      endLabel.setAttribute('y', endNode.y - 12);
      endLabel.setAttribute('text-anchor', 'middle');
      endLabel.setAttribute('font-size', '7');
      endLabel.setAttribute('fill', '#fff');
      endLabel.setAttribute('font-weight', 'bold');
      endLabel.setAttribute('pointer-events', 'none');
      endLabel.textContent = endLabelText;
      routeGroup.appendChild(endLabel);

      // Auto-zoom to the whole corridor the route occupies.
      const routePts = [startNode, ...waypoints, endNode];
      const rx = routePts.map(p => p.x);
      const ry = routePts.map(p => p.y);
      const margin = 12;
      zoomToSvgBox({
        x: Math.min(...rx) - margin,
        y: Math.min(...ry) - margin,
        width: (Math.max(...rx) - Math.min(...rx)) + margin * 2,
        height: (Math.max(...ry) - Math.min(...ry)) + margin * 2,
      });
      setIsProcessingFilter(false);
    }, 50); // Small delay to allow the loading UI to render

    return () => clearTimeout(timer);
  }, [routeFromBoothId, routeToBoothId, booths, zoomToSvgBox]);


  if (!layout || !layout.svgContent) {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="ri-alert-line me-2"></i>
        No SVG content available for this layout.
      </div>
    );
  }

  // Hall -> Zone -> Booth. With a zone chosen, only its stalls are offered.
  const hallBooths = filterHallId ? booths.filter(b => b.HallID === filterHallId) : [];
  const filteredBooths = zoneBoothKeys
    ? hallBooths.filter(b => zoneBoothKeys.has(normalizeBoothKey(b.BoothName)))
    : hallBooths;

  const zoneStallCount = zoneBoothKeys ? zoneBoothKeys.size : 0;
  // Zone is a convenience, not a step: "All Zones" offers the whole hall.
  // Only the hall is required, since it is what scopes the booth list at all.
  const boothSelectDisabled = !filterHallId;

  // Everything the popup can truthfully say about a booth. The layout API's
  // booths[] carries only BoothID/Name/ClipId/Hall/Status, so zone comes from
  // the zone mapping and vendor detail from the work-updation tracker.
  const boothDetail = (() => {
    const empty = { zone: null, stallId: null, services: [], tasks: [], exhibitors: [] };
    if (!selectedBooth) return empty;

    const key = normalizeBoothKey(selectedBooth.BoothName);
    const zoneRow = (zoneStalls || []).find(s => normalizeBoothKey(s.StallName) === key);
    const hallKey = String(selectedBooth.HallName || "").trim().toLowerCase();
    if (!hallKey) return { ...empty, zone: zoneRow ? zoneRow.Zone : null };

    const inHall = (r) => String(r.HallName || "").trim().toLowerCase() === hallKey;

    // Exhibitors: one row per deliverable x registration, so collapse to one
    // entry per exhibitor and keep the services they're affected by.
    const byExhibitor = new Map();
    (exhibitorRows || []).filter(inHall).forEach((r) => {
      const id = r.ExhRegistrationID != null ? r.ExhRegistrationID : r.ExhRegistrationNo;
      if (id == null) return;
      if (!byExhibitor.has(id)) {
        byExhibitor.set(id, {
          id,
          company: firstValue(r.CompanyName),
          regNo: r.ExhRegistrationNo,
          email: r.Email,
          mobile: firstValue(r.MobileNo),
          stallType: r.StallTypeName,
          stallArea: r.StallArea,
          services: new Set(),
        });
      }
      const svcName =
        r.name ||
        (r.ServiceID != null ? serviceNameById.get(String(r.ServiceID)) : null);
      if (svcName) byExhibitor.get(id).services.add(svcName);
    });

    return {
      zone: zoneRow ? zoneRow.Zone : null,
      stallId: (zoneRow && zoneRow.StallId) != null ? zoneRow.StallId : selectedBooth.StallId,
      services: (trackerRows || []).filter(inHall),
      tasks: (taskRows || []).filter(inHall),
      exhibitors: Array.from(byExhibitor.values()),
    };
  })();

  const selectedServiceName = selectedServiceId
    ? (() => {
        const s = (services || []).find(x => String(x.ServiceID) === String(selectedServiceId));
        return s ? s.Name || s.DisplayName || "" : "";
      })()
    : "";

  // With a service selected the headline status reports that service, not the
  // booth's overall one — otherwise the header and the service row underneath
  // it contradict each other ("InProgress" over "Air: Pending").
  const popupStatus = boothDisplayStatus(selectedBooth);

  // Legend entries: the canonical list for stable colour + order, plus any
  // status the tracker actually returned that isn't in it, so a new status
  // value from the backend is never silently dropped.
  const legendItems = STATUS_LEGEND.concat(
    Object.keys(statusCounts)
      .filter(s => !STATUS_LEGEND.some(i => i.status === s))
      .map(s => ({ status: s, color: getStatusColor(s) }))
  );

  const handleWheel = (e) => {
    e.preventDefault();
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    const pointX = e.clientX - rect.left;
    const pointY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    zoomAtPoint(transform.current.scale * delta, pointX, pointY);
    updateTransformStyle();
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      transform.current.isDragging = true;
      transform.current.hasDragged = false;
      transform.current.dragStart = { x: e.clientX, y: e.clientY };
      transform.current.dragInitial = { x: e.clientX, y: e.clientY };
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e) => {
    if (transform.current.isDragging) {
      const dxTotal = e.clientX - transform.current.dragInitial.x;
      const dyTotal = e.clientY - transform.current.dragInitial.y;
      if (Math.abs(dxTotal) > 5 || Math.abs(dyTotal) > 5) {
        transform.current.hasDragged = true;
        hasUserAdjustedRef.current = true;
      }

      const dx = e.clientX - transform.current.dragStart.x;
      const dy = e.clientY - transform.current.dragStart.y;
      transform.current.offsetX += dx;
      transform.current.offsetY += dy;
      transform.current.dragStart = { x: e.clientX, y: e.clientY };
      updateTransformStyle();
    }
  };

  const handleMouseUp = () => {
    transform.current.isDragging = false;
    if (containerRef.current) containerRef.current.style.cursor = 'grab';
  };

  const zoomIn = () => {
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAtPoint(transform.current.scale * 1.2, rect.width / 2, rect.height / 2);
    updateTransformStyle(true);
  };

  const zoomOut = () => {
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAtPoint(transform.current.scale * 0.8, rect.width / 2, rect.height / 2);
    updateTransformStyle(true);
  };

  const handleReset = () => {
    setIsProcessingFilter(true);
    setFilterMessage("Resetting layout view & filters...");

    hasUserAdjustedRef.current = false;
    if (!applyDefaultView(true)) {
      transform.current.scale = 1;
      transform.current.offsetX = 0;
      transform.current.offsetY = 0;
      updateTransformStyle(true);
    }

    drawFocusOverlay(null);
    if (onFilterHallChange) onFilterHallChange("");
    if (onFilterBoothChange) onFilterBoothChange("");
    if (onZoneChange) onZoneChange("");
    if (onServiceChange) onServiceChange("");
    setRouteFromBoothId("");
    setRouteToBoothId("");
    setRouteError("");
    setFocusError("");

    setTimeout(() => {
      setIsProcessingFilter(false);
    }, 400);
  };

  const handleHallFilterChange = (e) => {
    const hallId = e.target.value;
    setIsProcessingFilter(true);
    setFilterMessage(hallId ? "Filtering & zooming to Hall..." : "Resetting hall filter...");
    if (onFilterHallChange) onFilterHallChange(hallId);
    if (onFilterBoothChange) onFilterBoothChange("");

    if (!hallId) {
      hasUserAdjustedRef.current = false;
      if (!applyDefaultView(true)) {
        transform.current.scale = 1;
        transform.current.offsetX = 0;
        transform.current.offsetY = 0;
        updateTransformStyle(true);
      }

      setTimeout(() => {
        setIsProcessingFilter(false);
      }, 400);
    }
  };

  const handleBoothFilterChange = (e) => {
    const boothId = e.target.value;
    setIsProcessingFilter(true);
    setFilterMessage(boothId ? "Focusing on selected Booth..." : "Viewing all booths in Hall...");
    if (onFilterBoothChange) onFilterBoothChange(boothId);
  };

  return (
    <div className="svg-canvas-container">
      {/* Toolbar */}
      <div className="svg-toolbar mb-3 d-flex gap-2 flex-wrap align-items-center">
        <div className="btn-group" role="group">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={zoomIn} title="Zoom In">
            <i className="ri-zoom-in-line"></i>
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled ref={zoomLabelRef}>
            100%
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={zoomOut} title="Zoom Out">
            <i className="ri-zoom-out-line"></i>
          </button>
        </div>

        {/* ─── Hall Filter Dropdown ─────────────────────────────────── */}
        <div className="d-flex align-items-center gap-1">
          <i className="ri-building-4-line text-muted" title="Filter by Hall"></i>
          <select
            className="form-control"
            style={{ minWidth: '130px', maxWidth: '160px' }}
            value={filterHallId || ""}
            onChange={handleHallFilterChange}
            title="Select Hall to zoom"
          >
            <option value="">All Halls</option>
            {halls.map((hall) => (
              <option key={hall.HallID} value={hall.HallID}>
                {hall.HallName} ({hall.BoothCount})
              </option>
            ))}
          </select>
        </div>

        {/* ─── Zone Filter Dropdown (cascades from Hall) ────────────── */}
        <div className="d-flex align-items-center gap-1">
          <i className="ri-shape-2-line text-muted" title="Filter by Zone"></i>
          <select
            className="form-control"
            style={{ minWidth: '130px', maxWidth: '160px' }}
            value={selectedZoneId || ""}
            onChange={(e) => onZoneChange && onZoneChange(e.target.value)}
            disabled={!filterHallId || isZoneLoading || zones.length === 0}
            title={
              !filterHallId ? "Select a Hall first"
                : isZoneLoading ? "Loading zones..."
                  : zones.length === 0 ? "No zones for this hall"
                    : "Select Zone"
            }
          >
            <option value="">
              {isZoneLoading ? "Loading zones..." : zones.length === 0 ? "No Zones" : "All Zones"}
            </option>
            {zones.map((zone) => (
              <option key={zone.HallZoneMappingID} value={zone.HallZoneMappingID}>
                {zone.Zone}
              </option>
            ))}
          </select>
        </div>

        {/* ─── Booth Filter Dropdown (cascades from Zone) ───────────── */}
        <div className="d-flex align-items-center gap-1">
          <i className="ri-store-2-line text-muted" title="Filter by Booth"></i>
          <select
            className="form-control"
            style={{ minWidth: '130px', maxWidth: '160px' }}
            value={filterBoothId || ""}
            onChange={handleBoothFilterChange}
            disabled={boothSelectDisabled}
            title={!filterHallId ? "Select a Hall first" : "Select Booth to zoom"}
          >
            <option value="">All Booths</option>
            {filteredBooths.map((booth) => (
              <option key={booth.BoothName} value={booth.BoothName}>
                {booth.BoothName}
              </option>
            ))}
          </select>
        </div>

        {/* Zone selected but none of its stalls are drawn in this layout. */}
        {selectedZoneId && zoneStallCount > 0 && filteredBooths.length === 0 && (
          <small className="text-warning-emphasis fw-semibold">
            <i className="ri-error-warning-line me-1"></i>
            None of this zone's {zoneStallCount} stalls appear in the drawing
          </small>
        )}

        {/* ─── Service Filter (fill list ActionID 49) ───────────────── */}
        {/* Filters the tracker server-side, so the legend counts, the work
            updation grid and the popup's service list all move together. */}
        <div className="d-flex align-items-center gap-1">
          <i className="ri-tools-line text-muted" title="Filter by Service"></i>
          <select
            className="form-control"
            style={{ minWidth: '130px', maxWidth: '160px' }}
            value={selectedServiceId || ""}
            onChange={(e) => onServiceChange && onServiceChange(e.target.value)}
            disabled={services.length === 0}
            title={services.length === 0 ? "No services available" : "Filter by Service"}
          >
            <option value="">All Services</option>
            {services.map((svc) => (
              <option key={svc.ServiceID} value={svc.ServiceID}>
                {svc.Name || svc.DisplayName}
              </option>
            ))}
          </select>
        </div>

        {/* ─── Route Finding Dropdowns ──────────────────────────────── */}
        {/* <div className="d-flex align-items-center gap-1 border-start ps-2 ms-1">
          <i className="ri-route-line text-primary" title="Route Preview"></i>
          <select
            className="form-control form-control-sm border-primary"
            style={{ minWidth: '120px', maxWidth: '140px' }}
            value={routeFromBoothId}
            onChange={(e) => setRouteFromBoothId(e.target.value)}
            title="Route From"
          >
            <option value="">Route From...</option>
            {booths.map((booth) => (
              <option key={`from_${booth.BoothName}`} value={booth.BoothName}>
                {booth.BoothName}
              </option>
            ))}
          </select>
          <span className="text-muted fw-bold">→</span>
          <select
            className="form-control form-control-sm border-primary"
            style={{ minWidth: '120px', maxWidth: '140px' }}
            value={routeToBoothId}
            onChange={(e) => setRouteToBoothId(e.target.value)}
            title="Route To"
          >
            <option value="">Route To...</option>
            {booths.map((booth) => (
              <option key={`to_${booth.BoothName}`} value={booth.BoothName}>
                {booth.BoothName}
              </option>
            ))}
          </select>
        </div> */}

        <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={handleReset} title="Reset View & Filters">
          <i className="ri-refresh-line me-1"></i>
          Reset
        </button>

        {isProcessingFilter && (
          <div className="d-flex align-items-center gap-2 text-primary fw-semibold bg-primary-subtle border border-primary-subtle px-2 py-1 rounded">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <small>{filterMessage || "Processing filter..."}</small>
          </div>
        )}
      </div>

      {/* ─── Focus (hall / booth) warning banner ─── */}
      {focusError && (
        <div className="alert alert-warning d-flex align-items-center py-2 px-3 mb-3" role="alert">
          <i className="ri-crosshair-line me-2"></i>
          <span className="small fw-semibold">{focusError}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            style={{ fontSize: '0.7rem' }}
            onClick={() => setFocusError("")}
            aria-label="Dismiss"
          ></button>
        </div>
      )}

      {/* ─── Route error banner ─── */}
      {routeError && (
        <div className="alert alert-warning d-flex align-items-center py-2 px-3 mb-3" role="alert">
          <i className="ri-error-warning-line me-2"></i>
          <span className="small fw-semibold">{routeError}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            style={{ fontSize: '0.7rem' }}
            onClick={() => setRouteError("")}
            aria-label="Dismiss"
          ></button>
        </div>
      )}

      {/* ─── Status legend: one compact row, sits directly above the map ─── */}
      {/* Counts come from getHallViewAuditTrackerList (HallStatus), passed in. */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-2 small text-muted">
        <span className="fw-semibold text-dark">
          <i className="ri-palette-line me-1"></i>Status
        </span>
        {legendItems.map((item) => (
          <span key={item.status} className="d-inline-flex align-items-center gap-1">
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: item.color,
                border: '1px solid #d1d5db',
                display: 'inline-block',
              }}
            />
            {item.status}
            {!isStatusLoading && statusCounts[item.status] > 0 && (
              <strong className="text-dark">{statusCounts[item.status]}</strong>
            )}
          </span>
        ))}
        {isStatusLoading && (
          <span className="d-inline-flex align-items-center gap-1">
            <span className="spinner-border spinner-border-sm" role="status" style={{ width: '0.7rem', height: '0.7rem' }}></span>
          </span>
        )}
      </div>

      {/* SVG Canvas — outer div measures the available width; the inner box is
          sized to the drawing's aspect ratio. */}
      <div ref={canvasWrapRef} style={{ width: "100%" }}>
      <div
        ref={containerRef}
        className="canvas-container rounded border border-secondary-subtle position-relative"
        style={{
          // Full width. The opening zoom and the cover-scale floor guarantee the
          // plan fills this box, so there is nothing to letterbox.
          width: "100%",
          height: `${canvasBox.height}px`,
          overflow: "hidden",
          cursor: "grab",
          backgroundColor: "#f8f9fa",
          userSelect: "none",
          position: "relative",
          touchAction: "none",
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {isProcessingFilter && (
          <div
            className="position-absolute d-flex align-items-center gap-2 bg-white shadow-sm border rounded-pill px-3 py-2"
            style={{
              top: "12px",
              left: "12px",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: "1rem", height: "1rem" }}>
              <span className="visually-hidden">Processing...</span>
            </div>
            <small className="fw-semibold text-dark">{filterMessage || "Processing filter..."}</small>
          </div>
        )}

        <MemoizedSvg ref={svgRef} svgContent={layout.svgContent} />

        {/* Floating Side Popup for Selected Booth Details */}
        {selectedBooth && (
          <div
            className="position-absolute shadow-lg bg-white rounded border border-secondary-subtle"
            style={{
              top: '16px',
              right: '16px',
              width: '360px',
              zIndex: 20,
              maxHeight: 'calc(100% - 32px)',
              overflowY: 'auto'
            }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light sticky-top">
              <h6 className="mb-0 fw-bold text-dark">
                <i className="ri-store-2-line me-2"></i>
                {selectedBooth.BoothName}
              </h6>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setSelectedBooth(null)}
              ></button>
            </div>
            <div className="p-3">
              <div className="mb-3">
                <span className="text-muted d-block small mb-1">
                  Status{selectedServiceName ? ` — ${selectedServiceName}` : ''}
                </span>
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: popupStatus.dimmed
                        ? DIMMED_DOT_COLOR
                        : getStatusColor(popupStatus.status)
                    }}
                  />
                  <span className={popupStatus.dimmed ? 'fw-semibold text-muted' : 'fw-semibold'}>
                    {popupStatus.dimmed
                      ? `No ${selectedServiceName || 'service'} record for this hall`
                      : popupStatus.status || 'Unknown'}
                  </span>
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <span className="text-muted d-block small">Hall</span>
                  <span className="fw-medium text-dark">
                    {selectedBooth.HallName || selectedBooth.HallID || '--'}
                  </span>
                </div>
                <div className="col-6">
                  <span className="text-muted d-block small">Zone</span>
                  <span className="fw-medium text-dark">{boothDetail.zone || '--'}</span>
                </div>
                <div className="col-6">
                  <span className="text-muted d-block small">Stall ID</span>
                  <span className="fw-medium text-dark">{boothDetail.stallId || '--'}</span>
                </div>
                <div className="col-6">
                  <span className="text-muted d-block small">Block</span>
                  <span className="fw-medium text-dark">{selectedBooth.Block || '--'}</span>
                </div>
              </div>

              {/* Tasks and exhibitors are recorded per hall, not per stall, so
                  both are labelled hall-level rather than implied to be this
                  booth's own. Source: sp_get_vendorauditTracker. */}
              <div className="border-top pt-3">
                <span className="text-muted d-block small mb-2 fw-semibold">
                  <i className="ri-list-check-2 me-1"></i>
                  Tasks in {selectedBooth.HallName || "this hall"}
                  {boothDetail.tasks.length > 0 && (
                    <span className="badge bg-secondary ms-2">{boothDetail.tasks.length}</span>
                  )}
                </span>

                {isTaskLoading ? (
                  <div className="text-muted small">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading tasks...
                  </div>
                ) : boothDetail.tasks.length === 0 ? (
                  <span className="text-muted fst-italic small">No tasks recorded for this hall.</span>
                ) : (
                  boothDetail.tasks.map((t, i) => {
                    const state = taskState(t);
                    const vendorCompany = lastValue(t.CompanyName);
                    const vendorMobile = lastValue(t.MobileNo);
                    const verification = verificationById[t.ExhVendorPlanDtlID] || null;
                    // The tracker row carries IsAdminApprove / IsCompleted of its
                    // own (scalar, and null when unset - earlier payloads sent them
                    // as [value, null] arrays, hence firstValue). Fall back to them
                    // when the task API did not return this deliverable, otherwise a
                    // verified row would read as Not Verified.
                    const isVerified = verification
                      ? !!verification.IsAdminApprove
                      : !!firstValue(t.IsAdminApprove);
                    return (
                      <div key={t.ExhVendorPlanDtlID != null ? `${t.ExhVendorPlanDtlID}_${i}` : i} className="mb-3 pb-2 border-bottom">
                        <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
                          <span className="fw-semibold text-dark small">{serviceNameOf(t) || "Service"}</span>
                          <span
                            className="badge flex-shrink-0"
                            style={{ backgroundColor: state.color, color: "#fff", fontWeight: 500 }}
                          >
                            {state.label}
                          </span>
                        </div>

                        {t.DeliverablesServiceName && (
                          <div className="small text-dark mb-1" style={{ lineHeight: 1.35 }}>
                            {t.DeliverablesServiceName}
                          </div>
                        )}

                        {(t.StartTime || t.EndTime) && (
                          <div className="small text-muted">
                            <i className="ri-time-line me-1"></i>
                            {shortDateTime(t.StartTime) || "--"} &rarr; {shortDateTime(t.EndTime) || "--"}
                          </div>
                        )}
                        {t.Overdue && (
                          <div className="small fw-semibold" style={{ color: getStatusColor("Delayed") }}>
                            <i className="ri-alarm-warning-line me-1"></i>{t.Overdue}
                          </div>
                        )}

                        <div className="small text-muted mt-1">
                          <i className="ri-building-4-line me-1"></i>{vendorCompany || "--"}
                        </div>
                        {t.vendorPersonName && (
                          <div className="small text-muted">
                            <i className="ri-user-3-line me-1"></i>{t.vendorPersonName}
                          </div>
                        )}
                        {vendorMobile && (
                          <div className="small text-muted">
                            <i className="ri-phone-line me-1"></i>
                            <a href={`tel:${vendorMobile}`} className="text-decoration-none text-muted">{vendorMobile}</a>
                          </div>
                        )}
                        {t.PrimaryEmail && (
                          <div className="small text-muted text-break">
                            <i className="ri-mail-line me-1"></i>
                            <a href={`mailto:${t.PrimaryEmail}`} className="text-decoration-none text-muted">{t.PrimaryEmail}</a>
                          </div>
                        )}

                        {/* Read-only here, and deliberately so. This payload
                            carries no IsHallManagerApprove, no sub points, no
                            remarks, no attachments and no approval stamps, so a
                            verify control on the map could only ever be a
                            partial copy of the task screen's columns - and a
                            half-shown verification state is worse than none.
                            The organizer is handed to that screen instead, with
                            this booth's rows already filtered in. */}
                        <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top gap-2">
                          <span className="small" style={{ color: isVerified ? getStatusColor("Completed") : "#6c757d" }}>
                            <i
                              className={isVerified ? "ri-checkbox-circle-fill me-1" : "ri-checkbox-blank-circle-line me-1"}
                              style={{ color: isVerified ? getStatusColor("Completed") : "#adb5bd" }}
                            ></i>
                            {isVerified ? "Verified" : "Not Verified"}
                            {isVerified && verification?.AdminApprovalDateTime && (
                              <span className="text-muted ms-1">
                                &middot; {verifiedOn(verification.AdminApprovalDateTime)}
                              </span>
                            )}
                          </span>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary flex-shrink-0"
                            style={{ fontSize: 11, padding: "2px 8px" }}
                            onClick={() => onOpenTaskDetail && onOpenTaskDetail({ ...t, name: serviceNameOf(t) })}
                            title="Open this hall's tasks in the task screen, where completion, both verifications, sub points, remarks and attachments all live"
                          >
                            <i className="ri-external-link-line me-1"></i>
                            Open in Tasks
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="border-top pt-3">
                <span className="text-muted d-block small mb-2 fw-semibold">
                  <i className="ri-group-line me-1"></i>
                  Exhibitors in {selectedBooth.HallName || "this hall"}
                  {boothDetail.exhibitors.length > 0 && (
                    <span className="badge bg-secondary ms-2">{boothDetail.exhibitors.length}</span>
                  )}
                </span>

                {isTaskLoading ? (
                  <div className="text-muted small">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading exhibitors...
                  </div>
                ) : boothDetail.exhibitors.length === 0 ? (
                  <span className="text-muted fst-italic small">No exhibitors recorded for this hall.</span>
                ) : (
                  <>
                    {boothDetail.exhibitors.slice(0, EXHIBITOR_PREVIEW).map((ex) => (
                      <div key={ex.id} className="mb-3 pb-2 border-bottom">
                        <div className="fw-semibold text-dark small">{ex.company || "--"}</div>
                        {ex.regNo && (
                          <div className="small text-muted"><i className="ri-hashtag me-1"></i>{ex.regNo}</div>
                        )}
                        {(ex.stallType || ex.stallArea != null) && (
                          <div className="small text-muted">
                            <i className="ri-layout-grid-line me-1"></i>
                            {ex.stallType || "Stall"}{ex.stallArea != null ? ` — ${ex.stallArea} sqm` : ""}
                          </div>
                        )}
                        {ex.mobile && (
                          <div className="small text-muted">
                            <i className="ri-phone-line me-1"></i>
                            <a href={`tel:${ex.mobile}`} className="text-decoration-none text-muted">{ex.mobile}</a>
                          </div>
                        )}
                        {ex.email && (
                          <div className="small text-muted text-break">
                            <i className="ri-mail-line me-1"></i>
                            <a href={`mailto:${ex.email}`} className="text-decoration-none text-muted">{ex.email}</a>
                          </div>
                        )}
                        {ex.services.size > 0 && (
                          <div className="small text-muted mt-1">
                            <i className="ri-tools-line me-1"></i>
                            {Array.from(ex.services).join(", ")}
                          </div>
                        )}
                      </div>
                    ))}
                    {boothDetail.exhibitors.length > EXHIBITOR_PREVIEW && (
                      <div className="small text-muted fst-italic">
                        + {boothDetail.exhibitors.length - EXHIBITOR_PREVIEW} more exhibitor(s)
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Tooltip (DOM updated directly via ref) */}
      <div
        ref={tooltipRef}
        style={{
          position: "fixed",
          display: "none",
          backgroundColor: "#1f2937",
          color: "white",
          padding: "8px 12px",
          borderRadius: "4px",
          fontSize: "12px",
          pointerEvents: "none",
          zIndex: 1000,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
          border: "1px solid #374151",
          maxWidth: "250px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          transform: "translateY(-100%)",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            ref={tooltipDotRef}
            style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
            }}
          />
          <span ref={tooltipContentRef}></span>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: "-5px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "5px solid #1f2937",
          }}
        />
      </div>

      {/* Info Section */}
      <div className="mt-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
        <div>
          <small className="text-muted d-block">
            <strong>Layout Name:</strong> {layout.LayoutName}
          </small>
          <small className="text-muted d-block">
            <strong>Halls in drawing:</strong> {halls.length}
            <span className="mx-2">|</span>
            <strong>Booths in drawing:</strong> {booths.length}
          </small>
        </div>

      </div>

    </div>
  );
};

export default SvgCanvas;